import { v4 as uuidv4 } from 'uuid';
import { createPureImageGridUtils } from './pureImageGrid.js';
import { LOCAL_ASSET } from './localAssets.js';

/** 未解锁时列表区展示：全部免费预览 + 1 个锁定占位 */
export const PAY_IMAGE_WALL_LOCKED_SUMMARY_KEY = 'locked-summary';

/** 新建组件默认图片数 */
export const PAY_IMAGE_WALL_DEFAULT_ITEM_COUNT = 2;

/** 展示用：纵向列表项高度（PC px） */
export const PAY_IMAGE_WALL_LIST_HEIGHT = 200;

/** 编辑弹窗内图片管理仍用 3 列宫格 */
const grid = createPureImageGridUtils({
	type: 'pay-image-wall',
	colCount: 3,
	colGap: 5,
	defaultHeight: 110,
	defaultBorderRadius: 10,
	defaultItemCount: PAY_IMAGE_WALL_DEFAULT_ITEM_COUNT,
	imageIdPrefix: 'pay-image-wall-item',
	randomStart: 301,
	fixedLocalImages: LOCAL_ASSET.payImageWall,
});

export const PAY_IMAGE_WALL_TYPE = grid.TYPE;
export const PAY_IMAGE_WALL_COL_COUNT = grid.COL_COUNT;
export const PAY_IMAGE_WALL_DEFAULT_HEIGHT = grid.DEFAULT_HEIGHT;
export const PAY_IMAGE_WALL_BATCH_SIZE = grid.BATCH_SIZE;

export const DEFAULT_PAY_IMAGE_WALL_FEATURES = [
	{ id: uuidv4(), text: '高清无水印', icon: 'check' },
	{ id: uuidv4(), text: '支持下载', icon: 'download' },
	{ id: uuidv4(), text: '永久有效', icon: 'shield' },
];

function getFixedFeatures() {
	return DEFAULT_PAY_IMAGE_WALL_FEATURES.map(item => ({ ...item, id: uuidv4() }));
}

function createPayWallItem(preset = {}, index = 0) {
	const localImgs = LOCAL_ASSET.payImageWall;
	const image = {
		id: `pay-image-wall-item-${uuidv4()}`,
		filename: 'demo.jpg',
		previewUrl: localImgs[index % localImgs.length],
		url: '',
		local: false,
	};
	return {
		id: uuidv4(),
		image,
	};
}

function normalizePayWallItem(item = {}, index = 0) {
	const fallback = createPayWallItem({}, index);
	const baseItem = grid.normalizeData({
		items: [{ ...item, id: item.id || fallback.id }],
	}).items[0];
	const image = baseItem.image?.previewUrl || baseItem.image?.url
		? baseItem.image
		: fallback.image;
	return {
		id: baseItem.id,
		image,
	};
}

function normalizePayWallItems(items = []) {
	if (!items.length) {
		return Array.from({ length: PAY_IMAGE_WALL_DEFAULT_ITEM_COUNT }, (_, index) => createPayWallItem({}, index));
	}
	return items.map((item, index) => normalizePayWallItem(item, index));
}

function normalizePayFields(data = {}) {
	const freePreviewCount = Number(data.freePreviewCount);
	const gridData = grid.normalizeData({
		...data,
		items: normalizePayWallItems(data.items),
	});

	return {
		title: data.title || '莫奈风格油画系列 ',
		subtitle: data.subtitle || `共 ${gridData.items.length} 张高清图片`,
		features: getFixedFeatures(),
		price: data.price ?? '3.90',
		currency: data.currency || '¥',
		buttonText: data.buttonText || '立即解锁',
		buttonBg: data.buttonBg || data.buttonBgStart || '#ff4d4f',
		freePreviewCount: Number.isFinite(freePreviewCount) && freePreviewCount >= 0
			? Math.min(freePreviewCount, gridData.items.length)
			: 1,
		...gridData,
		fullWidth: true,
	};
}

export function getPayImageWallDefaults(extra = {}) {
	return normalizePayFields({
		items: Array.from({ length: PAY_IMAGE_WALL_DEFAULT_ITEM_COUNT }, (_, index) => createPayWallItem({}, index)),
		freePreviewCount: 1,
		...extra,
	});
}

export function normalizePayImageWallData(data = {}) {
	return normalizePayFields(data);
}

export function isPayImageWallBlock(type) {
	return type === PAY_IMAGE_WALL_TYPE;
}

export function createDefaultPayImageWallItem(preset = {}, index = 0) {
	return createPayWallItem(preset, index);
}

export function createDefaultPayImageWallItems(count = PAY_IMAGE_WALL_BATCH_SIZE) {
	return Array.from({ length: count }, (_, index) => createPayWallItem({}, index));
}

export const getPayImageWallImageUrl = grid.getImageUrl;
export const getPayImageWallBlockStyle = grid.getBlockStyle;
export const getPayImageWallGalleryUrls = grid.getGalleryUrls;
export const getPayImageWallThumbAspectRatio = grid.getThumbAspectRatio;

export function getPayImageWallListEntries(data = {}, { unlocked = false } = {}) {
	const normalized = normalizePayImageWallData(data);
	const items = normalized.items;
	const pageCount = items.length;
	const freeCount = Math.min(normalized.freePreviewCount, pageCount);

	if (unlocked) {
		return items.map((item, index) => ({
			key: item.id,
			item,
			index,
			locked: false,
		}));
	}

	const entries = [];
	items.slice(0, freeCount).forEach((item, index) => {
		entries.push({
			key: item.id,
			item,
			index,
			locked: false,
		});
	});

	if (pageCount > freeCount) {
		const lockIndex = freeCount;
		entries.push({
			key: PAY_IMAGE_WALL_LOCKED_SUMMARY_KEY,
			item: items[lockIndex] || items[freeCount - 1] || { id: PAY_IMAGE_WALL_LOCKED_SUMMARY_KEY },
			index: lockIndex,
			locked: true,
		});
	}

	return entries;
}

export function getPayImageWallListStyle(data = {}, { h5 = false } = {}) {
	const normalized = normalizePayImageWallData(data);
	const pxRatio = h5 ? 50 : 1;
	return {
		borderRadius: h5 ? `${normalized.borderRadius / pxRatio}rem` : `${normalized.borderRadius}px`,
		itemGap: h5 ? `${10 / pxRatio}rem` : '10px',
		itemHeight: h5 ? `${PAY_IMAGE_WALL_LIST_HEIGHT / pxRatio}rem` : `${PAY_IMAGE_WALL_LIST_HEIGHT}px`,
	};
}

export function getPayImageWallUnlockKey(blockId) {
	return blockId ? `gedit-pay-wall-unlock-${blockId}` : '';
}

export function isPayImageWallUnlocked(blockId) {
	const key = getPayImageWallUnlockKey(blockId);
	if (!key || typeof sessionStorage === 'undefined') return false;
	return sessionStorage.getItem(key) === '1';
}

export function setPayImageWallUnlocked(blockId, unlocked = true) {
	const key = getPayImageWallUnlockKey(blockId);
	if (!key || typeof sessionStorage === 'undefined') return;
	if (unlocked) {
		sessionStorage.setItem(key, '1');
	} else {
		sessionStorage.removeItem(key);
	}
}
